import React, { useState } from 'react';
import { Box, Button, Link, Tooltip, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faEnvelope,
  faFile,
} from '@fortawesome/free-solid-svg-icons';
import {
  faGoogleScholar,
  faXTwitter,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import { newsData } from './NewsData';
import { NewsItem } from './NewsItem';
import { paperData, posterData } from './PubData';
import { PubItem } from './PubItem';
import { Section, SectionTitle } from './Section';
// import { ResearchOverview } from './ResearchOverview';
import { ServiceSection } from './ServiceSection';
import { Footer } from './Footer';

const profile = '/img/profile_xmas.JPG';
// Thesis overview illustration (web-sized copy of thesis.png, 900px wide).
// const thesisImage = '/img/thesis_web.png';
// const thesisAlt =
//   'Illustration of a person whose perception connects to interface elements placed in a 3D space';

library.add(faEnvelope, faFile, faGoogleScholar, faXTwitter, faLinkedin);

const isLatestNews = (dateStr) => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const today = new Date();
  const thisMonth = today.getMonth();
  const thisYear = today.getFullYear();
  const [newsMonth, newsYear] = dateStr.split(' ');

  if (Number.isNaN(Number(newsYear))) {
    return true;
  }

  if (thisYear - 1 > Number(newsYear)) {
    return false;
  }

  if (thisYear > Number(newsYear) && months.indexOf(newsMonth) < thisMonth) {
    return false;
  }

  return true;
};

// Use an explicit year when set (e.g., preprints), otherwise parse it from the venue.
const getPubYear = (paper) => {
  if (paper.year) {
    return paper.year;
  }
  const match = paper.venue && paper.venue.match(/(?:19|20)\d{2}/);
  return match ? match[0] : null;
};

const pubYearStyle = {
  fontSize: 40,
  fontWeight: 700,
  lineHeight: 1,
  color: 'rgba(177, 202, 128, 0.55)',
  letterSpacing: -0.5,
};

const ProfileLink = ({ icon, label, ariaLabel, href, onClick }) => (
  <Link
    href={href || '#'}
    onClick={onClick}
    aria-label={ariaLabel}
    target={href ? '_blank' : undefined}
    rel={href ? 'noopener noreferrer' : undefined}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#000000',
      textDecoration: 'none',
      cursor: 'pointer',
    }}
  >
    <FontAwesomeIcon
      icon={icon}
      fixedWidth
      style={{ fontSize: 13, marginRight: label ? 6 : 0 }}
    />
    {label}
  </Link>
);

const ResearchPage = ({ size }) => {
  const theme = useTheme();
  const [showAllNews, setShowAllNews] = useState(false);

  const anchorStyle = {
    fontWeight: 400,
    textDecoration: 'underline',
    textDecorationColor: theme.customColors.brand,
    color: theme.palette.text.primary,
  };

  const bannerLinkStyle = {
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    textDecorationColor: theme.customColors.brand,
  };

  const subtleAnchorStyle = {
    fontWeight: 400,
    color: theme.palette.text.primary,
  };

  const newsItems = showAllNews
    ? newsData.news
    : newsData.news.filter((item) => isLatestNews(item.date));

  const isSmall = size === 'small' || size === 'medium';
  const [emailTooltip, setEmailTooltip] = useState('hyunsung@cs.cmu.edu — Click to copy');

  const handleCopyEmail = (event) => {
    event.preventDefault();
    navigator.clipboard.writeText('hyunsung@cs.cmu.edu');
    setEmailTooltip('Copied!');
    setTimeout(() => setEmailTooltip('hyunsung@cs.cmu.edu — Click to copy'), 2000);
  };

  /* Marker (paint) highlight styles, currently unused.
  const markerHighlightStyle = {
    display: 'inline',
    fontWeight: 700,
    background: `linear-gradient(
        104deg,
        rgba(177, 202, 128, 0) 0.9%,
        rgba(177, 202, 128, 0.95) 2.4%,
        rgba(177, 202, 128, 0.55) 5.8%,
        rgba(177, 202, 128, 0.14) 93%,
        rgba(177, 202, 128, 0.72) 96%,
        rgba(177, 202, 128, 0) 98%
      ),
      linear-gradient(
        183deg,
        rgba(177, 202, 128, 0) 0%,
        rgba(177, 202, 128, 0.32) 7.9%,
        rgba(177, 202, 128, 0) 15%
      )`,
    padding: '0.08em 0.34em 0.18em',
    lineHeight: '1.75em',
    borderRadius: 6,
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
    textShadow: `-8px 8px 8px rgba(177, 202, 128, 0.32),
      12px -10px 6px rgba(248, 247, 245, 0.95),
      -10px -14px 20px rgba(248, 247, 245, 0.9)`,
  };

  const secondaryMarkerHighlightStyle = {
    display: 'inline',
    fontWeight: 700,
    // background: `linear-gradient(
    //     104deg,
    //     rgba(177, 202, 128, 0) 1%,
    //     rgba(177, 202, 128, 0.42) 3%,
    //     rgba(177, 202, 128, 0.2) 8%,
    //     rgba(177, 202, 128, 0.08) 93%,
    //     rgba(177, 202, 128, 0.28) 96%,
    //     rgba(177, 202, 128, 0) 99%
    //   ),
    //   linear-gradient(
    //     183deg,
    //     rgba(177, 202, 128, 0) 0%,
    //     rgba(177, 202, 128, 0.16) 8%,
    //     rgba(177, 202, 128, 0) 15%
    //   )`,
    // padding: '0.03em 0.18em 0.1em',
    // lineHeight: '1.6em',
    // borderRadius: 4,
    // boxDecorationBreak: 'clone',
    // WebkitBoxDecorationBreak: 'clone',
  };
  */

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Section id="about">
        <Box
          display="flex"
          flexDirection={isSmall ? 'column' : 'row'}
          alignItems={isSmall ? 'flex-start' : 'stretch'}
        >
          <Box
            width={isSmall ? '100%' : 360}
            minWidth={isSmall ? 'auto' : 360}
            mr={isSmall ? 0 : 3}
            mb={isSmall ? 2 : 0}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Box
              component="img"
              src={profile}
              alt="Hyunsung Cho"
              width={isSmall ? '80%' : '100%'}
              maxWidth={360}
              style={{
                borderRadius: 4,
                marginBottom: 16,
              }}
            />
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
              mb={2}
              style={{ columnGap: 14, rowGap: 6 }}
            >
              <ProfileLink
                icon="fa-solid fa-file"
                label="CV"
                href="/pdf/CV_HyunsungCho.pdf"
              />
              <ProfileLink
                icon="fa-brands fa-google-scholar"
                label="Google Scholar"
                href="https://scholar.google.co.kr/citations?user=VpQp9hEAAAAJ"
              />
              <Typography
                component="span"
                style={{ fontSize: '0.875rem', color: theme.palette.divider }}
              >
                |
              </Typography>
              <Tooltip title={emailTooltip} arrow>
                <span>
                  <ProfileLink
                    icon="fa-solid fa-envelope"
                    ariaLabel="Copy email"
                    onClick={handleCopyEmail}
                  />
                </span>
              </Tooltip>
              <Tooltip title="LinkedIn" arrow>
                <span>
                  <ProfileLink
                    icon="fa-brands fa-linkedin"
                    ariaLabel="LinkedIn"
                    href="https://www.linkedin.com/in/hyunsung-cho-548976192"
                  />
                </span>
              </Tooltip>
              <Tooltip title="X (formerly Twitter)" arrow>
                <span>
                  <ProfileLink
                    icon="fa-brands fa-x-twitter"
                    ariaLabel="X (formerly Twitter)"
                    href="https://twitter.com/hciresearcher"
                  />
                </span>
              </Tooltip>
            </Box>
            <Box
              style={{
                maxHeight: isSmall ? 150 : 300,
                overflowY: 'auto',
                borderTop: `1px solid ${theme.palette.divider}`,
                paddingTop: 12,
              }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                News, Talks, and Travels
              </Typography>
              {newsItems.map((news) => (
                <NewsItem
                  key={`${news.date}-${news.text}`}
                  size="small"
                  date={news.date}
                  text={news.text}
                  compact
                />
              ))}
              <Box display="flex" justifyContent="center" mt={1}>
                <Button
                  color="primary"
                  onClick={() => setShowAllNews(!showAllNews)}
                  size="small"
                  style={{ textTransform: 'none' }}
                >
                  {showAllNews ? 'Show Latest' : 'Show All'}
                </Button>
              </Box>
            </Box>
          </Box>
          <Box flexGrow={1}>
            <Box
              mb={2}
              px={2}
              py={1.25}
              display="flex"
              flexDirection={isSmall ? 'column' : 'row'}
              alignItems={isSmall ? 'flex-start' : 'center'}
              justifyContent="space-between"
              style={{
                backgroundColor: '#F3F6ED',
                borderRadius: 4,
                gap: isSmall ? 4 : 16,
              }}
            >
              <Box>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  On the job market for 2026–2027
                </Typography>
                <Typography variant="body2">
                  I am seeking tenure-track faculty and research scientist
                  positions.
                  I’d be happy to hear about opportunities that might be a good
                  fit!
                </Typography>
              </Box>
              <Typography
                variant="body2"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                <Link
                  href="/pdf/CV_HyunsungCho.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={bannerLinkStyle}
                >
                  CV
                </Link>
                {' · '}
                <Link href="mailto:hyunsung@cs.cmu.edu" style={bannerLinkStyle}>
                  Email
                </Link>
              </Typography>
            </Box>
            <Typography paragraph>
              I am a Ph.D. candidate at the{' '}
              <Link
                href="https://www.hcii.cmu.edu"
                target="_blank"
                rel="noopener noreferrer"
                style={anchorStyle}
              >
                Human-Computer Interaction Institute
              </Link>{' '}
              in{' '}
              <Link
                href="https://www.cs.cmu.edu"
                target="_blank"
                rel="noopener noreferrer"
                style={anchorStyle}
              >
                Carnegie Mellon University's School of Computer Science
              </Link>
              {/* , where she is a member of the{' '}
              <Link
                href="https://augmented-perception.org"
                target="_blank"
                rel="noopener noreferrer"
                style={anchorStyle}
              >
                Augmented Perception Lab
              </Link> */}
              , advised by{' '}
              <Link
                href="https://davidlindlbauer.com"
                target="_blank"
                rel="noopener noreferrer"
                style={anchorStyle}
              >
                Prof. David Lindlbauer
              </Link>
              . I am also a Student Researcher at Google.
            </Typography>
            {/*
            {!isSmall && (
              <Box
                component="img"
                src={thesisImage}
                alt={thesisAlt}
                style={{
                  float: 'right',
                  width: '34%',
                  maxWidth: 280,
                  margin: '4px 0 12px 24px',
                }}
              />
            )}
            */}
            <Box
              mb={2}
              px={2.5}
              pt={2}
              pb={1}
              style={{
                border: '1px solid rgba(177, 202, 128, 0.6)',
                borderRadius: 4,
              }}
            >
              <Typography paragraph>
                I build <b>perceptually intelligent interfaces for spatial computing</b> that adapt to what people can see, hear, and do.
                In spatial computing, such as extended reality (XR), people are moving, their surroundings are changing, and their attention is shared with physical tasks.
                My research helps them access digital information and assistance while staying engaged with the physical world.
              </Typography>
              <Box component="ul" mt={0} mb={2} pl={3}>
                <Typography component="li" style={{ marginBottom: 8 }}>
                  <b>Modeling perception for adaptive spatial interfaces.</b> I build
                  computational models that simulate how people look, listen, and
                  move (<Link
                  href="https://augmented-perception.org/publications/2026-sensonaut.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  Sensonaut
                </Link>), and develop
                  model-based interaction techniques that reduce errors and
                  visual clutter in spatial interfaces (<Link
                  href="https://augmented-perception.org/publications/2024-auptimize.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  Auptimize
                </Link>,{' '}
                <Link
                  href="https://augmented-perception.org/publications/2024-sonohaptics.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  SonoHaptics
                </Link>,{' '}
                <Link
                  href="https://augmented-perception.org/publications/2023-blendmr.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  BlendMR
                </Link>).
                </Typography>
                <Typography component="li">
                  <b>Enabling contextual assistance with multimodal AI.</b> I
                  develop interaction techniques and systems to access AI
                  assistance during everyday tasks, such as a smart-glasses
                  assistant that remembers user intent so repeated requests take
                  just a glance and a gesture (<Link
                  href="https://augmented-perception.org/publications/2025-persistant_assistant.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  Persistent Assistant
                </Link>).
                  I also build representations and benchmarks that help spatial
                  interfaces offer relevant functionality based on user goals and
                  context (<Link
                  href="https://augmented-perception.org/publications/2024-minexr.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  MineXR
                </Link>,{' '}
                <Link
                  href="https://augmented-perception.org/publications/2026-contextXR.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={anchorStyle}
                >
                  ContextXR
                </Link>).
                </Typography>
              </Box>
            </Box>
            <Typography paragraph>
              My work has been published in leading HCI and XR venues, including CHI, UIST, TVCG, IMWUT, and CSCW, and has received Best Paper Awards and a Methods Recognition.
              My research has been supported by the{' '}
              <Link
                href="https://linksim.org/"
                target="_blank"
                rel="noopener noreferrer"
                style={anchorStyle}
              >
                Link Foundation Modeling, Simulation &amp; Training Fellowship
              </Link>{' '}
              and sponsored research from Meta Reality Labs.
              Previously, I held research positions at Meta Reality Labs, Nokia Bell Labs, and Aalto University.
              I received my M.S. and B.S. degrees from the{' '}
              <Link
                href="https://cs.kaist.ac.kr/"
                target="_blank"
                rel="noopener noreferrer"
                style={anchorStyle}
              >
                KAIST School of Computing
              </Link>.
            </Typography>
            {/*
            {isSmall && (
              <Box
                component="img"
                src={thesisImage}
                alt={thesisAlt}
                style={{
                  display: 'block',
                  width: '70%',
                  maxWidth: 320,
                  margin: '0 auto 16px',
                }}
              />
            )}
            */}
            {/* Previous bio paragraph on past labs, kept for reference.
            <Typography paragraph>
            Her research has been shaped by experiences across both industry research labs and international academic environments, including 
            Computational Behavior Lab at Aalto University with Prof.{" "}
            <Link
              href="https://users.aalto.fi/~oulasvir/"
              target="_blank"
              rel="noopener noreferrer"
              style={anchorStyle}
            >
              Antti Oulasvirta
            </Link>
            , Meta Reality Labs with Dr.{" "}
            <Link
              href="https://www.kashyaptodi.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={anchorStyle}
            >
              Kashyap Todi
            </Link>{" "}
            , Nokia Bell Labs with Dr.{" "}
            <Link
              href="https://akhilmathurs.github.io/"
              target="_blank"
              rel="noopener noreferrer"
              style={anchorStyle}
            >
              Akhil Mathur
            </Link>
            , Networking & Mobile Systems Lab at KAIST with Prof.{" "}
            <Link
              href="https://sites.google.com/site/wewantsj/"
              target="_blank"
              rel="noopener noreferrer"
              style={anchorStyle}
            >
              Sung-Ju Lee
            </Link>
            , and KIXLAB at KAIST with Prof.{" "}
            <Link
              href="https://juhokim.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={anchorStyle}
            >
              Juho Kim
            </Link>
            . Across these experiences, her earlier work explored context-aware computing for digital wellbeing, reducing digital distractions, and privacy-preserving approaches to adaptive systems.
            </Typography> */}
          </Box>
        </Box>
      </Section>

      {/* <Section id="research">
        <SectionTitle>Research</SectionTitle>
        <ResearchOverview size={size} />
      </Section> */}

      <Section id="publications">
        <SectionTitle>Publications</SectionTitle>
        {paperData.papers.map((paper, index) => {
          const year = getPubYear(paper);
          const isFirstOfYear =
            index === 0 ||
            getPubYear(paperData.papers[index - 1]) !== year;

          return (
            <Box key={paper.title} display="flex" flexDirection="column">
              {isSmall && isFirstOfYear && year && (
                <Typography style={{ ...pubYearStyle, fontSize: 28 }}>
                  {year}
                </Typography>
              )}
              <Box display="flex" flexDirection="row">
                {!isSmall && (
                  <Box width={120} flexShrink={0}>
                    {isFirstOfYear && year && (
                      <Typography style={pubYearStyle}>{year}</Typography>
                    )}
                  </Box>
                )}
                <Box flexGrow={1} minWidth={0}>
                  <PubItem {...paper} size={size} />
                </Box>
              </Box>
            </Box>
          );
        })}
        <Typography variant="h5" gutterBottom>
          Posters, Demos, Videos, and Workshop Papers
        </Typography>
        {posterData.posters
          .filter((poster) => poster.venue.includes('Doctoral Symposium'))
          .map((poster) => (
            <PubItem
              key={poster.title}
              {...poster}
              size={size}
            />
          ))}
        <Box
          display="grid"
          style={{
            gridTemplateColumns: isSmall ? '1fr' : '1fr 1fr',
            columnGap: 32,
          }}
        >
          {posterData.posters
            .filter((poster) => !poster.venue.includes('Doctoral Symposium'))
            .map((poster) => (
              <PubItem
                key={poster.title}
                {...poster}
                size={size}
              />
            ))}
        </Box>
      </Section>

      <Section id="service">
        <SectionTitle>Recognitions &amp; Service</SectionTitle>
        <ServiceSection size={size} />
      </Section>

      <Footer />
    </Box>
  );
};

export { ResearchPage };
